import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

import { adminApi } from '../../api/clients';
import { colour, secondaryButtonSx } from '../admin/tokens';
import ConfirmationActions from './ConfirmationActions';

/**
 * The re-enrolment's documents — the confirmation the family was emailed, and for a manual entry the
 * signed scan and the form — each to view or download.
 *
 * Fetched with the reviewer's own sign-in, like the card pictures: a plain link cannot carry the
 * staff token. The server finds each file from the re-enrolment itself.
 */
const ReviewDocuments = ({ sessionId, parentId }) => {
    const [documents, setDocuments] = useState(null);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');

    const [reloads, setReloads] = useState(0);

    useEffect(() => {
        if (!sessionId) return undefined;
        let ignore = false;
        adminApi.get('/reviewDocuments', { params: { SessionID: sessionId } })
            .then(({ data }) => { if (!ignore) setDocuments(data?.documents || []); })
            .catch(() => { if (!ignore) setDocuments((current) => current || []); });
        return () => { ignore = true; };
    }, [sessionId, reloads]);

    const open = async (document, download) => {
        const key = `${document.kind}-${download ? 'download' : 'view'}`;
        setBusy(key);
        setError('');
        // Opened before the file arrives, so the browser treats it as the click it was and does not
        // block it as a pop-up.
        const viewer = download ? null : window.open('', '_blank');
        try {
            const response = await adminApi.get('/reviewDocument', {
                params: { SessionID: sessionId, kind: document.kind, ...(download ? { download: 1 } : {}) },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(response.data);
            if (download) {
                const link = window.document.createElement('a');
                link.href = url;
                link.download = fileNameFrom(response.headers?.['content-disposition']) || `${document.label}.pdf`;
                window.document.body.appendChild(link);
                link.click();
                link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            } else if (viewer) {
                viewer.location.href = url;
            }
        } catch {
            if (viewer) viewer.close();
            setError(`Could not open the ${document.label.toLowerCase()}.`);
        } finally {
            setBusy('');
        }
    };

    if (documents === null) {
        return <CircularProgress size={16} sx={{ color: colour.primary, mb: 2 }} />;
    }
    if (!documents.length) return null;

    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: colour.body, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.25 }}>
                Documents
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '12px' }}>{error}</Alert>}
            {documents.map((document) => (
                <Box
                    key={document.kind}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', p: 1.25, mb: 1, border: `1px solid ${colour.border}`, borderRadius: '10px' }}
                >
                    <PictureAsPdfOutlinedIcon sx={{ color: colour.muted }} />
                    <Typography sx={{ flex: 1, fontSize: '14px', color: colour.body, minWidth: 180 }}>{document.label}</Typography>
                    {/* A confirmation listed with exists: false has no file yet, only a rebuild. */}
                    {document.exists !== false && (
                        <>
                            <Button size="small" sx={secondaryButtonSx} disabled={Boolean(busy)} onClick={() => open(document, false)}>
                                {busy === `${document.kind}-view` ? <CircularProgress size={14} /> : 'View'}
                            </Button>
                            <Button size="small" sx={secondaryButtonSx} disabled={Boolean(busy)} onClick={() => open(document, true)}>
                                {busy === `${document.kind}-download` ? <CircularProgress size={14} /> : 'Download'}
                            </Button>
                        </>
                    )}
                    {document.kind === 'confirmation' && (
                        <ConfirmationActions
                            document={document}
                            parentId={parentId}
                            dataLogId={sessionId}
                            // Re-read the list, so View and Download fetch the rebuilt file and a
                            // confirmation that had none now offers them.
                            onRegenerated={() => setReloads((count) => count + 1)}
                        />
                    )}
                </Box>
            ))}
        </Box>
    );
};

const fileNameFrom = (disposition) => {
    const match = /filename="([^"]+)"/.exec(String(disposition || ''));
    return match ? match[1] : '';
};

export default ReviewDocuments;
